import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { SlidersHorizontal, Map as MapIcon, ChevronDown, Bookmark, X, Check } from 'lucide-react';

import FilterModal from '../components/FilterModal';
import GoogleMapView from '../components/GoogleMapView';
import SearchingDots from '../components/SearchingDots';
import GymCard from '../components/GymCard';
import GymCardSkeleton from '../src/components/directory/GymCardSkeleton';
import EmptyState from '../components/directory/EmptyState';
import { Button } from '../src/components/ui/button';
import { Input } from '../src/components/ui/input';
import { Badge } from '../src/components/ui/badge';
import { ListboxSelect } from '../src/components/ui/listbox-select';

import { Gym, FilterState, ViewMode, SavedSearch } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useDirectoryContext } from '../contexts/DirectoryContext';
import { useDirectoryFilters } from '../hooks/useDirectoryFilters';
import { useDirectorySearch } from '../hooks/useDirectorySearch';
import { useRouteStateSlug, useRouteStateSlugOrNull } from '../hooks/useRouteStateSlug';
import type { StateSlug } from '../lib/stateCore.mjs';
import { useDirectoryResults, GYMS_PER_PAGE } from '../hooks/useDirectoryResults';
import { useDirectoryFavourites } from '../hooks/useDirectoryFavourites';
import { useDirectorySavedSearches } from '../hooks/useDirectorySavedSearches';


import { buildGymUrl, normalizeLocationDisplay } from '../lib/slugs';
import { ModalPortal } from '../src/components/ui/modal-portal';
import { DEFAULT_STATE, stateDisplayToSlug, stateSlugToDisplay } from '../lib/stateCore.mjs';
import { hasDirectoryFor } from '../lib/directoryCoverage';
import { getStateMapCenter, DEFAULT_MAP_ZOOM, buildStateDirectoryRootUrl } from '../lib/stateRoutes';
import { getDirectoryMetadata, applyRuntimeDirectoryRobots } from '../lib/seo';
import { SeoHead } from '../components/SeoHead';
import { getSSGPageData } from '../lib/ssGData';
import { styleSectionHeading } from '../lib/suburbStyleLinks';
import { resolveLocationLabel, matchedPostcodeDisplay } from '../lib/locationLabel';
import { useBreakpointUp } from '../hooks/useBreakpointUp';
import { buildFilteredSearchUrl, buildSavedSearchUrl, savedSearchLocationLabel } from '../lib/searchNavigation';

interface DirectoryPageProps {
    isVisible?: boolean;
    onGymSelect?: (gym: Gym) => void;
}

type SortOption = 'distance' | 'rating' | 'reviews';


/**
 * ⭐ THE LIST-VIEW WIDTH CAP, shared by the card grid AND the count/filter-pill row above it
 * (operator-ruled 2026-08-17).
 *
 * ⚠️ WHY IT IS A CONSTANT RATHER THAN TWO COPIES. With the map hidden, the grid was capped and
 * centred at 1120px while the row above it was not — so on a wide window the count sat at the
 * far page-left and the pills at the far page-right, with the cards floating between them.
 * Two elements that must share an edge were expressing that edge twice. **A rule written
 * twice is a rule that drifts** — this stream has paid for that three times today alone (a
 * coverage rule live on 2 of ~4,100 routes; an auto-open rule true in 1 of 9 emitters; a
 * ranking rule mirrored across a package boundary). One constant, two consumers.
 *
 * ⚠️ LIST VIEW ONLY. With the map shown the grid takes no cap — it shares the row with the map
 * and is already flush with the count — so applying this there would INTRODUCE the misalignment
 * it exists to remove.
 */
const LIST_VIEW_WIDTH_CAP = 'max-w-[1120px] mx-auto';

/** A removable active-filter pill rendered on the results view. */
const FilterChip: React.FC<{ label: string; onRemove: () => void }> = ({ label, onRemove }) => (
    /* Shared "category" Badge variant — same brand accent as the blog category pill
       (PostCard) so pills read consistently across the app. */
    <Badge variant="category" className="pr-1">
        <span className="truncate max-w-[12rem]">{label}</span>
        <button
            type="button"
            aria-label={`Remove ${label} filter`}
            onClick={onRemove}
            className="rounded-full p-0.5 text-brand-primary/70 hover:bg-brand-primary/20 hover:text-brand-primary transition-colors"
        >
            <X size={12} />
        </button>
    </Badge>
);


const DirectoryPage: React.FC<DirectoryPageProps> = ({
    onGymSelect,
    isVisible = true,
}) => {
    const {
        activeFilters,
        setActiveFilters,
        cachedSearchResults,
        setCachedSearchResults,
        cachedSearchLocation,
        setCachedSearchLocation,
        cachedSearchCenter,
        setCachedSearchCenter,
    } = useDirectoryContext();
    const navigate = useNavigate();
    const location = useLocation();
    const { user, openAuthModal } = useAuth();
    // Map-layout breakpoint: below `lg` → full-screen map/list toggle; at lg+ → split
    // (list + map). Kept in sync with FavouritesPage so the map/list feature is consistent
    // across both pages at every viewport.
    // Item 1: asks the browser the same REM question the stylesheet asks, instead of
    // comparing useWindowSize's pixel width to 1024 — those disagree for a visitor
    // with a raised default font size. The hook keeps the SSR/hydration contract
    // (first render uses the prerender answer, exactly as useWindowSize does).
    const isNarrowViewport = !useBreakpointUp('lg');

    // 1. Hooks
    // UI-45 (operator-ruled 2026-08-08): the map shows WHEN THERE ARE GYMS TO PLOT and
    // is hidden when there are none — so an empty directory no longer presents an empty
    // map pane. Below lg the page keeps its existing LIST/MAP toggle semantics and is
    // unchanged by this item.
    //
    // HYDRATION: this initial value is chosen during the FIRST render, which IS the SSG
    // render, so it may only read things present at SSR. `getSSGPageData()` is exactly
    // that (the injected __SSG_DATA__ script); nav state is NOT (see hasMounted below).
    // Consequence, deliberate, with the real numbers (review-corrected — my first note
    // understated the blast radius by two orders of magnitude): every one of the 1,433
    // prerendered suburb/style payloads carries gyms.length > 0, so those pages still bake
    // SPLIT and UI-45 changes none of their bytes. What DOES flip to LIST is every page
    // whose payload carries NO gyms — the 9 untagged static pages and `/gyms`, PLUS all
    // 2,762 gym-profile and 18 blog pages (payload present, `gyms: []`) — i.e. ~2,789 of
    // 4,222 prerendered files change markup. On all of them the overlay is hidden anyway,
    // and NO anchor is added or removed (the map subtree contains zero links, verified),
    // so the standing no-link-loss rule holds. NOTE for anyone diffing dist after this
    // ships: UI-46 in the same slice changes the overlay container's class on ALL pages,
    // so a raw diff will show the 1,433 as changed too — that is UI-46, not this.
    const ssGPageData = useMemo(() => getSSGPageData(), []);
    const hasPrerenderedGyms = (ssGPageData?.gyms?.length ?? 0) > 0;
    const [viewMode, setViewMode] = useState<ViewMode>(
        isNarrowViewport || !hasPrerenderedGyms ? ViewMode.LIST : ViewMode.SPLIT
    );
    // Set once the visitor works the Show/Hide Map control themselves. From that moment
    // the automatic rule stops overriding them for the rest of the session — this is the
    // operator-ruled "stay open" stickiness: having opened the map, browsing on to another
    // suburb keeps it open (and equally, having closed it keeps it closed).
    const userChoseViewRef = useRef(false);
    const [sortBy, setSortBy] = useState<SortOption>('distance');
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    // Client-mount gate. The postcode SEED is read from location.state (see displaySuburb),
    // but reading nav state during the FIRST (hydration) render would diverge from the SSG
    // HTML — which has no nav state — and trip a React hydration mismatch. Gating on
    // hasMounted makes the hydration render match SSR (bare suburb); the seed is folded in
    // only after mount. This overlay is PERSISTENT (mounted once, above <Routes>), so by the
    // time any in-app navigation happens hasMounted is already true → no flick on that path;
    // only a true hydration render (direct load / reload) briefly waits for mount.
    const [hasMounted, setHasMounted] = useState(false);
    useEffect(() => { setHasMounted(true); }, []);
    // Search-progress-bar timing (ms, all tunable). Completion is IDENTICAL for
    // suburb and filter searches: reach 100% → hold at full blue → drain back to
    // the grey line, mirroring the fill.
    // UI-128 (loading spec RULED C3, 2026-08-24) — 700/500/700 = 1,900ms → 450/300/450
    // = 1,200ms. The flourish decorated ~50ms of measured work for 1.9 seconds, ~38x.
    //
    // ⚠️⚠️ HOLD_MS AND DRAIN_MS ARE SHARED BY BOTH BARS — the comment below calls this
    // "the shared completion sequence" and means it literally. Branch A (a filter-only
    // tweak) runs fill→hold→drain; branch B (a real network search) trickles toward 90%
    // and is then sent to the SAME hold→drain when the search settles. So these two
    // numbers are the search bar's completion tail as well, which this change shortens
    // from 1,200ms to 750ms. That was ruled deliberately, not inherited: C1/C2 require
    // the two bars to look identical, and giving them different finishing speeds would
    // be precisely the visible distinction C1/C2 rejected. Change one, change both.
    //
    // ⚠️ HOLD_MS IS NOT THE PAUSE. The 'hold' phase carries its own 150ms snap-to-100%
    // transition, so the motionless part is HOLD_MS − 150 = 150ms. That is ON the floor
    // the C3 reasoning named (below ~150ms a hold stops reading as a pause and the bar
    // reads as a flicker), not below it — and it was ruled with that stated. If you ever
    // shorten HOLD_MS further, the pause disappears before the number looks alarming.
    //
    // ⚠️ Pinned by pages/DirectoryPage.barDurations.test.tsx — behaviourally over the
    // shared tail, and as a source tripwire on these three values and their total.
    const FILTER_BAR_MS = 450;  // deterministic fill for an instant filter-only tweak
    const HOLD_MS = 300;        // pause at full blue to acknowledge completion
    const DRAIN_MS = 450;       // retract/drain 100%→0% (mirror of the fill)
    // Search progress bar phase:
    //  idle → active (network search — trickle toward 90%) → hold (snap to 100% +
    //  pause) → drain (retract to the grey line). 'fill' is the deterministic
    //  0→100% fill for an instant filter-only refinement, then it joins hold→drain.
    const [barPhase, setBarPhase] = useState<'idle' | 'active' | 'fill' | 'hold' | 'drain'>('idle');
    const [highlightedGymId, setHighlightedGymId] = useState<string | null>(null);
    const [hoveredGymId, setHoveredGymId] = useState<string | null>(null);

    // Map keyed by gym.id; the ref callback deletes on unmount (null) so entries
    // for gyms no longer in paginatedGyms don't accumulate (D7).
    const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());

    const { filters, setFilters, currentSearchKey } = useDirectoryFilters({ initialFilters: activeFilters, isVisible });

    // R-02: the state this directory route is scoped to — passed to every
    // state-capable search call so a VIC page queries the VIC corpus, not NSW.
    const routeStateSlug = useRouteStateSlug();
    // Items 6/7 (2026-08-17): the HONEST state for coverage COPY — null off a state
    // route, where `routeStateSlug` above would answer DEFAULT_STATE. Both come from
    // one classifier; see hooks/useRouteStateSlug.ts for why the two arms exist.
    const routeStateSlugOrNull = useRouteStateSlugOrNull();

    // SSG: read pre-fetched gyms injected by the build hook (null in dev / client-only).
    // NOTE: declared ABOVE with the view-mode state — UI-45 derives the initial map/list
    // choice from it, and that must happen during the first (SSG) render.

    const {
        allGyms,
        searchCenter,
        isAutoSearching,
        isMapMoving,
        isRevealed,
        resolvedLocation,
        setIsMapMoving
    } = useDirectorySearch({
        filters,
        currentSearchKey,
        cachedResults: cachedSearchResults,
        cachedSearchLocation,
        setCachedSearchLocation,
        cachedSearchCenter,
        setCachedSearchCenter,
        isVisible,
        gymName: filters.gymName,
        ssGInitialGyms: ssGPageData?.gyms,
        // UI-131 Part D — the build's suburb centre. On a cold arrival this is now the ONLY
        // source: the mount reconcile that used to fetch it is gone. Undefined on any route
        // whose payload is not a directory one, which is correct — the state hubs have no
        // suburb, and `stateMapCenter` at wide zoom is the right answer there.
        ssgSearchCenter: ssGPageData?.searchCenter,
        stateSlug: routeStateSlug
    });

    // UI-25: the map's pre-search centre follows the route's state — a Victorian
    // page opens on Melbourne, not Sydney. One shared table (lib/stateRoutes);
    // the two per-file NSW_CENTER copies this replaced could drift independently.
    const stateMapCenter = useMemo(() => getStateMapCenter(routeStateSlug), [routeStateSlug]);
    const mapCenterCoords = useMemo((): [number, number] => [stateMapCenter.lat, stateMapCenter.lng], [stateMapCenter]);
    // UI-57 state-root mode — LIVE since the flip (2026-08-14, buildStateHubUrl moved
    // last): non-null exactly on '/{state}/gyms', the suburb-less state search page.
    // No auto-search fires there BECAUSE the shared suburb parse requires the
    // '/{state}/:suburb/gyms' shape (review-caught FATAL — the old inline parse read
    // '/vic/gyms' as a suburb named "Gyms"), and the map rests on
    // getStateMapCenter(routeStateSlug). Guarded by the mounted tests in
    // components/directory/StateDirectorySwitch.test.tsx.
    // 🪦 `const stateRootSlug = useMemo(() => stateDirectoryRootSlug(location.pathname), …)`
    // LIVED HERE. Its last two consumers were the state pills (removed 2026-08-16) and the
    // coverage line below (re-keyed 2026-08-17, items 6/7) — `tsc` named it unused the moment
    // the second went, and it traced to nothing else.
    // ⚠️ THE TOMBSTONE FURTHER DOWN QUOTES THIS NAME VERBATIM, so a grep for `stateRootSlug`
    // still returns hits and they are ALL PROSE. Do not read them as a live derivation.
    // ⚠️ AND DO NOT REINSTATE IT FOR COVERAGE COPY: `stateDirectoryRootSlug` is non-null only
    // on the bare `/{state}/gyms` root, which is what limited the state-aware wording to 2
    // routes while ~4,100 suburb/style/age/experience pages fell through. Route-segment
    // derivation (`useRouteStateSlugOrNull`) is the correct anchor and is used below.

    // UI-72 — a SUBURB page's first page must cover the whole suburb, so that every
    // gym the build knows about is reachable by an anchor from its own suburb page.
    //
    // ⚠️ SCOPE, stated because a reader will over-generalise it: this fires ONLY on a
    // prerendered `location` route whose payload belongs to THIS url. It deliberately
    // does NOT fire on the state directories (/{state}/gyms returns ~1,493 gyms, where
    // the 12-cap is the whole point), on the /gyms chooser, or on style pages — measured
    // 2026-08-15: 0 of 437 style pages render more than 12 gym links, so they do not
    // have this defect and are not being "fixed" speculatively.
    //
    // The route-scoping mirrors `isPrerendered` below and exists for the same reason:
    // the __SSG_DATA__ script survives client-side navigation, so an unscoped read
    // would size suburb B's first page from suburb A's payload. Both SSG and the first
    // client render compute this from the same pathname + payload, so hydration agrees.
    // Returns 0 — "no opinion" — for every route that is not this suburb's own page,
    // and the hook's Math.max floor turns that into GYMS_PER_PAGE. Deliberately NOT
    // `: GYMS_PER_PAGE` here: restating the default made this memo depend on that
    // import on EVERY render, which broke DirectoryPage.filters.test.tsx, whose mock
    // of the hook module does not re-export the constant. Expressing "no opinion"
    // instead of repeating the default is both smaller and the reason the floor is
    // load-bearing rather than defensive.
    const suburbFirstPageSize = useMemo(() => {
        const routeSuburbSlug = location.pathname.split('/')[2] ?? '';
        const isOwnLocationPayload =
            !!ssGPageData &&
            ssGPageData.type === 'location' &&
            ssGPageData.locationSlug === routeSuburbSlug;
        return isOwnLocationPayload ? (ssGPageData.gyms?.length ?? 0) : 0;
    }, [location.pathname, ssGPageData]);

    const {
        sortedGyms,
        paginatedGyms,
        handleLoadMore,
        showGym,
        hasMore,
        resultsSettled
    } = useDirectoryResults({
        allGyms,
        filters,
        searchCenter,
        userLocation: null,
        sortBy,
        mapCenter: mapCenterCoords,
        initialVisibleCount: suburbFirstPageSize
    });

    const { favouriteIds, handleFavouriteToggle, loadingFavouriteId } = useDirectoryFavourites();

    const {
        savedSearches,
        isSavingSearch,
        isSaveSearchModalOpen,
        setIsSaveSearchModalOpen,
        saveSearchName,
        setSaveSearchName,
        isSavedSearchesDropdownOpen,
        setIsSavedSearchesDropdownOpen,
        currentSearchMatchesSaved,
        handleSaveSearch,
        handleUnsaveSearch,
    } = useDirectorySavedSearches(filters, viewMode);

    // 2. Effects
    const lastSyncedResultsRef = useRef<string>('');

    useEffect(() => {
        const resultsJson = JSON.stringify(allGyms.map(g => g.id).sort());
        if (!isAutoSearching && resultsJson !== lastSyncedResultsRef.current) {
            lastSyncedResultsRef.current = resultsJson;
            setCachedSearchResults(allGyms);
        }
    }, [allGyms, setCachedSearchResults, isAutoSearching]);

    useEffect(() => {
        setActiveFilters(filters);
    }, [filters, setActiveFilters]);

    // Drive the search progress bar from the REAL search lifecycle (not a fixed
    // timer): trickle while a network search is running, then snap to 100% + fade
    // the moment the gyms are revealed.
    //
    // ⚠️ HOISTED ABOVE THE RESIZE EFFECT 2026-08-16, and the move is not cosmetic. That
    // effect now reads this value, and a dependency array is evaluated DURING RENDER — so
    // naming `isSearchActive` in its deps while the const still sat 50 lines below would
    // have thrown a temporal-dead-zone ReferenceError on the first render of every
    // directory page. Leaving it out of the deps instead would have "worked" via a stale
    // closure that happens to be refreshed by the other deps, which is a bug waiting for
    // someone to change an unrelated dependency. Declared before its first use instead.
    const isSearchActive = isAutoSearching || (!!filters.suburb && !isRevealed);

    const lastIsNarrowRef = useRef(isNarrowViewport);
    useEffect(() => {
        if (isNarrowViewport !== lastIsNarrowRef.current) {
            lastIsNarrowRef.current = isNarrowViewport;
            // UI-45: crossing the breakpoint no longer FORCES the map open at lg+ — it
            // re-applies the SAME rule the effect below uses, otherwise a resize would
            // reinstate the very empty map pane that item removed. An explicit user choice
            // still wins.
            // ⚠️ Updated 2026-08-16 with the ruling below: the rule is now "an active search
            // OR results", not "results" alone. These two must stay in step — when they
            // disagreed the map's state depended on whether you had resized, which is the
            // class of bug that made UI-45 necessary in the first place.
            if (userChoseViewRef.current) return;
            setViewMode(
                isNarrowViewport || (!isSearchActive && sortedGyms.length === 0)
                    ? ViewMode.LIST
                    : ViewMode.SPLIT,
            );
        }
    }, [isNarrowViewport, sortedGyms.length, isSearchActive]);

    useEffect(() => {
        setHighlightedGymId(null);
        setHoveredGymId(null);
    }, [currentSearchKey]);

    useEffect(() => {
        // UI-57 note: links targeting '/gyms' still hand this flag over, but /gyms is
        // now the CHOOSER (not overlay space), so isVisible is false there and the
        // flag is INERT on those arrivals — deliberately left in the emitters rather
        // than stripped across ten files for zero behaviour change (register (+54)).
        // It still fires normally on directory-space targets (/{state}/… links).
        // ⭐ ITEM 8.1 (operator-ruled 2026-08-17): OPEN UNLESS A SUBURB IS ALREADY CHOSEN.
        // The flag means "this arrival may need filters"; whether it actually does is a
        // question about the page's own state, so the PAGE decides rather than each
        // emitter. Landing with no suburb has nothing to show until one is picked, so the
        // modal is help; landing WITH one — a suburb page, or a style page like
        // /nsw/parramatta-2150/gyms/bjj, which carries a suburb — already has results on
        // screen, and a modal over them is an obstacle.
        //
        // ⚠️ WHY THE CHECK LIVES HERE AND NOT AT THE NINE EMITTERS. Only the blog links
        // computed `!suburb`; the other eight passed `true` unconditionally, which is how
        // the rule ended up true in one place and false in eight. One derivation cannot
        // drift; nine copies already had.
        //
        // ⚠️ `filters.suburb` is safe to read at arrival — useDirectoryFilters parses it
        // from the pathname SYNCHRONOUSLY in its state initialiser, so there is no window
        // where a suburb route reads as suburb-less and pops the modal anyway.
        //
        // ⚠️ Skipping deliberately does NOT clear the flag, and that is safe rather than
        // sloppy: every filter change navigates with a REPLACED state object
        // (`state: { filters: newFilters }`, :486/:527), so a stale triggerFilters cannot
        // survive to fire once the visitor clears the suburb.
        if (isVisible && location.state?.triggerFilters && !isFilterModalOpen && !filters.suburb) {
            const timer = setTimeout(() => {
                setIsFilterModalOpen(true);
                // Clear the state so it doesn't re-open on every re-mount
                // ⚠️ `location.search` IS PRESERVED, and dropping it was a real defect.
                // This read `navigate(location.pathname, …)`, which strips the query string:
                // arriving at `/nsw/gyms?styles=BJJ` with the filter flag silently discarded
                // the BJJ filter the visitor had just followed a link for. Pre-existing, and
                // narrowed by item 8.1 to suburb-less arrivals only — but suburb-less URLs are
                // exactly the ones that carry filters as query params, so the narrowing made
                // it MORE likely to bite, not less. Fixed rather than left as a footnote
                // (operator-ruled 2026-08-17).
                navigate(location.pathname + location.search, { replace: true, state: { ...location.state, triggerFilters: false } });
            }, 500);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [isVisible, location.state, isFilterModalOpen, navigate, filters.suburb]);

    useEffect(() => {
        if (!isVisible && isFilterModalOpen) {
            setIsFilterModalOpen(false);
        }
    }, [isVisible, isFilterModalOpen]);

    // ⭐ THE MAP OPENS WHEN A SEARCH STARTS, not when results arrive (operator-ruled
    // 2026-08-16). This PARTIALLY REVERSES UI-45 (2026-08-08) and the reversal was made
    // with UI-45's own cost in front of the operator, so the reasoning is recorded here
    // rather than quietly overwritten.
    //
    // UI-45 hid the map unless there were gyms to plot, because an empty directory used to
    // present an empty map pane — and because the map is a BILLABLE Google Maps
    // instantiation, so every appearance costs money. The operator chose the narrowest of
    // the three readings of "present when gyms are being searched": it opens the moment a
    // suburb or filter is chosen, NOT on every page view. That bounds the billing to real
    // searches, which was the deciding factor.
    //
    // ⚠️ WHAT SURVIVES OF UI-45, deliberately: the IDLE state. A settled search with no
    // results still closes the map, so `/nsw/gyms` before anyone searches, and a search
    // that genuinely found nothing, both show no map pane. Only the WINDOW changed — the
    // map now covers the search instead of arriving after it.
    //
    // Declared HERE, below isSearchActive, because it depends on it.
    // Guards, each load-bearing and each UNCHANGED by this ruling:
    //  · narrow viewports own their LIST/MAP toggle — never touched here;
    //  · an explicit Show/Hide Map press freezes the rule for the session;
    //  · `!isVisible` keeps the rule ROUTE-SCOPED: this overlay is mounted on every route,
    //    so without it a hidden instance would recompute from cached results and arrive at
    //    a directory route with a view decided by a different page.
    //
    // ⚠️ The mid-search flap UI-45's old comment warned about is now IMPOSSIBLE rather than
    // guarded against: the map is open for the whole of an active search, so the transient
    // empty list can no longer slam it shut and re-open it a moment later.
    useEffect(() => {
        if (!isVisible || isNarrowViewport || userChoseViewRef.current) return;
        if (isSearchActive || sortedGyms.length > 0) {
            setViewMode(ViewMode.SPLIT);
        } else {
            setViewMode(ViewMode.LIST);
        }
    }, [sortedGyms.length, isNarrowViewport, isSearchActive, isVisible]);

    const wasSearchActiveRef = useRef(false);
    // The bar must mean "a search is running" and nothing else. On a cold load of any
    // directory URL the suburb is already set from the path and nothing is revealed
    // yet, so isSearchActive is true on the very first render — which ran the whole
    // fill/hold/drain animation on EVERY page load, including loads that do no
    // user-initiated search at all (the results are prerendered; the hydrate
    // reconcile is deliberately quiet). This ref suppresses that first pass and
    // arms the bar once the page has settled.
    const initialSettleDoneRef = useRef(false);
    useEffect(() => {
        if (!initialSettleDoneRef.current) {
            // First pass: the page is painting its prerendered results, not searching.
            // Mutation-proven: removing this guard fails
            // pages/DirectoryPage.progressBar.test.tsx's cold-load assertion.
            if (!isSearchActive) initialSettleDoneRef.current = true;
            wasSearchActiveRef.current = false;
            return;
        }
        if (isSearchActive) {
            setBarPhase('active');
        } else if (wasSearchActiveRef.current) {
            setBarPhase('hold');   // search done → snap to 100% + hold, then drain
        }
        wasSearchActiveRef.current = isSearchActive;
    }, [isSearchActive]);

    // Advance the bar through the shared completion sequence: a filter 'fill' runs
    // for FILTER_BAR_MS then joins completion; 'hold' snaps to 100% and pauses for
    // HOLD_MS; 'drain' retracts 100%→0% over DRAIN_MS back to the grey line.
    useEffect(() => {
        if (barPhase === 'fill') {
            const t = setTimeout(() => setBarPhase('hold'), FILTER_BAR_MS);
            return () => clearTimeout(t);
        }
        if (barPhase === 'hold') {
            const t = setTimeout(() => setBarPhase('drain'), HOLD_MS);
            return () => clearTimeout(t);
        }
        if (barPhase === 'drain') {
            const t = setTimeout(() => setBarPhase('idle'), DRAIN_MS);
            return () => clearTimeout(t);
        }
        return undefined;
    }, [barPhase]);

    // 3. Handlers
    const handleApplyFilters = (newFilters: FilterState, resolvedDisplay?: string, resolvedState?: StateSlug) => {
        window.scrollTo(0, 0);
        // A change that alters the SEARCH (suburb/radius/name) kicks off a real
        // network search, whose bar is driven by isSearchActive above. A filter-ONLY
        // refinement (styles/age/experience) runs in-memory with no network, so give
        // it a brief deterministic bar here so the change still gets visible feedback.
        const searchKeyOf = (f: FilterState) => `${f.suburb}|${f.radius}|${f.useRadius}|${f.gymName ?? ''}`;
        const changed = JSON.stringify(newFilters) !== JSON.stringify(filters);
        // Only the instant filter-only fill; if a real network search is already in
        // flight (isSearchActive), leave the bar in its 'active' trickle rather than
        // faking a 'done' fill while the fetch is still pending.
        if (changed && searchKeyOf(newFilters) === searchKeyOf(filters) && !isSearchActive) {
            setBarPhase('fill');
        }
        setFilters(newFilters);

        // Sync URL
        if (newFilters.suburb) {
            // Single style → canonical path-based URL; multiple → query param (noindex)
            // THE STATE COMES FROM THIS PAGE'S OWN PATH, not from the default (blocker B1).
            // Omitting it built a first-state URL on a second-state page, and the
            // navigate() below fired on it — sending the visitor to another state's real,
            // built page with replace:true destroying the way back.
            // UI-24: routeStateSlug (useRouteStateSlug, declared above) is the ONE canonical
            // derivation of that state — same classifier, same DEFAULT_STATE arm for
            // state-less routes (the overlay also mounts on non-directory routes). This
            // replaced an inline findLaunchedStateIndex re-derivation; EQ's TS-H4 guard
            // tests the consolidated result.
            // ITEM 6 SLICE 1: prefer the state the LOCATION resolved to over the route's.
            // They differ exactly when geolocation lands in another state — the case that
            // silently built an NSW address around a Victorian suburb name. Undefined for
            // a typed suburb, where the route's state remains correct and historical.
            const targetState = resolvedState ?? routeStateSlug;
            // Item 3: was a character-identical copy of buildFilteredSearchUrl —
            // same base-path rule, same params, same order. Routed through the
            // shared builder so the copies cannot drift apart again.
            const newUrl = buildFilteredSearchUrl(newFilters, targetState);
            if (location.pathname + location.search !== newUrl) {
                // Carry the optimistic "Suburb Postcode" so the pill shows the postcode
                // immediately (see displaySuburb). When the caller supplies none — e.g. a
                // filter-chip removal via removeFilter — preserve the label already on the page
                // (from the live result or the current seed) so the change doesn't drop the seed
                // and re-introduce the flick, including when it resets resolvedLocation. Guarded
                // by matchedPostcodeDisplay so a mismatched or stale label can't leak through.
                const cleanNewSuburb = newFilters.suburb ? normalizeLocationDisplay(newFilters.suburb) : newFilters.suburb;
                const carriedDisplay = resolvedDisplay
                    ?? matchedPostcodeDisplay(resolvedLocation, cleanNewSuburb)
                    ?? matchedPostcodeDisplay(
                        (location.state as { resolvedDisplay?: string } | null)?.resolvedDisplay,
                        cleanNewSuburb,
                    );
                navigate(newUrl, {
                    replace: true,
                    state: carriedDisplay ? { filters: newFilters, resolvedDisplay: carriedDisplay } : { filters: newFilters },
                });
            }
        } else {
            // UI-53 (operator-ruled 2026-08-11): filters armed WITHOUT a location used to
            // write nothing at all — the whole sync above sat behind `if (suburb)` with no
            // else — so a refresh or a shared link dropped the visitor's selections and
            // reopened the panel blank. Nothing is lost from a RESULT list (with no suburb
            // the page renders the choose-a-location prompt and never searches); what was
            // lost was the selection itself.
            //
            // A NEW EXPLICIT ARM, deliberately, rather than relaxing the gate above: the
            // with-suburb path must stay byte-identical, because it is the canonical,
            // indexable URL shape and every existing consumer depends on its exact bytes.
            //
            // ⚠️ RADIUS IS DELIBERATELY NOT WRITTEN. A radius has no meaning without a
            // centre point, and the read side sets useRadius=true from any `?radius=`,
            // which would light a "Within N km" chip on a page that has no location.
            //
            // ⚠️ AND THIS NAVIGATION CARRIES NO ROUTER STATE, which is load-bearing. The
            // URL now holds styles/age/exp and the URL→filters sync parses them ungated by
            // suburb, so state is unnecessary — and attaching it would re-arm exactly the
            // defect UI-52 just closed (an entry carrying state.filters switches that sync
            // off, and the dedupe then decides what a Back press is allowed to restore).
            const params = new URLSearchParams();
            if (newFilters.styles.length > 0) params.set('styles', newFilters.styles.join(','));
            if (newFilters.ageGroups.length > 0) params.set('age', newFilters.ageGroups.join(','));
            if (newFilters.experienceLevels.length > 0) params.set('exp', newFilters.experienceLevels.join(','));

            const queryString = params.toString();
            // UI-57 (two review catches, one per slice): the no-suburb write keeps the
            // PAGE'S OWN state on EVERY directory route — first caught on the state
            // root itself, then the tranche review caught the sharper form: keying on
            // stateRootSlug alone left all 988 SUBURB pages writing '/gyms', so
            // removing the suburb chip on /vic/box-hill-3128/gyms dumped the visitor
            // onto the chooser (state lost, replace:true destroying the way back).
            // routeStateSlug is route-derived and every overlay-visible route carries
            // a state post-flip, so this is total; state-less surfaces never run it.
            const noSuburbBase = buildStateDirectoryRootUrl(routeStateSlug);
            const newUrl = queryString ? `${noSuburbBase}?${queryString}` : noSuburbBase;
            if (location.pathname + location.search !== newUrl) {
                navigate(newUrl, { replace: true });
            }
        }
    };

    const handleClearAll = () => {
        setFilters({ styles: [], ageGroups: [], experienceLevels: [], suburb: '', radius: 10, useRadius: false, gymName: '' });
        // Same state-keeping rule as handleApplyFilters' no-suburb arm (UI-57).
        navigate(buildStateDirectoryRootUrl(routeStateSlug), { replace: true });
    };

    // Active-filter chips: removal re-applies via the SAME path as a modal Apply
    // (handleApplyFilters → setFilters + suburb-gated URL write + worker re-run). If a
    // removal would leave NO active filter, fall back to the canonical clear path.
    const hasActiveFilter = (f: FilterState) =>
        !!f.suburb || f.styles.length > 0 || f.ageGroups.length > 0 ||
        f.experienceLevels.length > 0 || f.useRadius || !!f.gymName;

    const removeFilter = (next: FilterState) => {
        if (hasActiveFilter(next)) handleApplyFilters(next);
        else handleClearAll();
    };

    const handleViewToggle = useCallback(() => {
        if (isNarrowViewport) {
            // ⚠️ DELIBERATELY NOT setting userChoseViewRef here. Narrow's toggle is a
            // DIFFERENT AXIS (LIST<->MAP full-screen), not "do I want the split map".
            // Latching on it let ViewMode.MAP survive a resize past 1024, where MAP is
            // not renderable: the list column takes the `w-0 opacity-0 invisible` arm
            // and EVERY control — including this very button — lives inside it, so the
            // user got a full-bleed map with no way back that only a hard reload
            // cleared (the overlay is persistent). Review REAL-BUG; leaving the ref
            // clear lets the breakpoint effect normalise MAP -> SPLIT/LIST on crossing.
            setViewMode(viewMode === ViewMode.LIST ? ViewMode.MAP : ViewMode.LIST);
        } else {
            // UI-45: at lg+ this button IS the "show/hide the map" preference, so an
            // explicit press stops the automatic has-gyms rule overriding the visitor
            // for the rest of the session.
            userChoseViewRef.current = true;
            setViewMode(viewMode === ViewMode.SPLIT ? ViewMode.LIST : ViewMode.SPLIT);
        }
    }, [isNarrowViewport, viewMode]);

    const handleGymSelect = useCallback((gym: Gym) => {
        if (onGymSelect) {
            onGymSelect(gym);
        } else {
            // Addressed from THIS GYM's state, not the page's: a radius search near a
            // border legitimately returns gyms from more than one state, so a per-page
            // state would file some of them under a state they are not in.
            const gymUrl = buildGymUrl(
                gym.name, gym.stableId || gym.id, gym.suburb, gym.postcode,
                gym.state ? stateDisplayToSlug(gym.state) : DEFAULT_STATE,
            );
            navigate(gymUrl, { state: { gym } });
        }
    }, [onGymSelect, navigate]);

    const handleMarkerClick = useCallback((gymId: string | null) => {
        if (!gymId) {
            setHighlightedGymId(null);
            return;
        }

        // Ensure gym is visible in results
        showGym(gymId);

        // Highlight the gym
        setHighlightedGymId(gymId);

        // Ensure list is visible on mobile
        if (isNarrowViewport) {
            setViewMode(ViewMode.LIST);
        }
    }, [isNarrowViewport, showGym]);

    // Reactive scrolling effect: triggers when a gym is highlighted AND its card is rendered
    useEffect(() => {
        if (highlightedGymId && isRevealed && !isAutoSearching && viewMode !== ViewMode.MAP) {
            // Small delay to ensure the DOM has updated and layout has settled
            const timer = setTimeout(() => {
                const cardEl = cardRefs.current.get(highlightedGymId);
                if (cardEl) {
                    cardEl.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                        inline: 'nearest'
                    });
                }
            }, 100);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [highlightedGymId, paginatedGyms, isRevealed, isAutoSearching, viewMode]);

    // Stable per-gym handler factory (DF3): GymCard is React.memo'd, so passing
    // fresh inline closures every render would defeat memoisation. We cache one
    // set of callbacks per gym.id in a ref and reuse them across renders. The
    // closures avoid capturing changing values two ways: highlightedGymId via the
    // functional setState form, and handleGymSelect/handleFavouriteToggle (which
    // changes when favourites mutate) via a latest-ref, so the cached closures
    // never need to be rebuilt and stay identity-stable.
    const latestGymActionsRef = useRef({ handleGymSelect, handleFavouriteToggle });
    latestGymActionsRef.current = { handleGymSelect, handleFavouriteToggle };

    const gymHandlersRef = useRef<Map<string, {
        onClick: () => void;
        onMouseEnter: () => void;
        onMouseLeave: () => void;
        onFavouriteToggle: () => void;
    }>>(new Map());

    const getGymHandlers = useCallback((gym: Gym) => {
        const cache = gymHandlersRef.current;
        const existing = cache.get(gym.id);
        if (existing) return existing;
        const handlers = {
            onClick: () => latestGymActionsRef.current.handleGymSelect(gym),
            onMouseEnter: () => setHoveredGymId(gym.id),
            onMouseLeave: () => {
                setHoveredGymId(null);
                setHighlightedGymId(prev => (prev === gym.id ? null : prev));
            },
            onFavouriteToggle: () => latestGymActionsRef.current.handleFavouriteToggle(gym),
        };
        cache.set(gym.id, handlers);
        return handlers;
    }, []);

    // Prune the per-gym handler cache to the current result set so it can't grow
    // unbounded as searches change (mirrors the cardRefs cleanup in D7).
    useEffect(() => {
        const liveIds = new Set(sortedGyms.map(g => g.id));
        for (const id of gymHandlersRef.current.keys()) {
            if (!liveIds.has(id)) gymHandlersRef.current.delete(id);
        }
    }, [sortedGyms]);

    const handleApplySavedSearch = (search: SavedSearch) => {
        // UI-01: the search's OWN stored state (stamped at save time), not this
        // page's — applying a first-state saved search from a second-state page
        // must not relabel it. Legacy documents carry no state and were always
        // applied against DEFAULT_STATE, so the fallback preserves them exactly.
        // Item 3: the SAME saved search loaded from SavedSearchesPage used to
        // produce a DIFFERENT address, because that page wrote the params in a
        // different order from this one. Both now call buildSavedSearchUrl, which
        // also owns the UI-01 state fallback these two used to repeat.
        navigate(buildSavedSearchUrl(search));
        // UI-45: restoring a search's STORED view is an explicit preference, exactly
        // like pressing the toggle — latch it, or the has-gyms rule overrides it a
        // second later when the results land and silently discards SavedSearch.viewMode
        // (review REAL-BUG: it made the stored field effectively dead at lg+).
        userChoseViewRef.current = true;
        setViewMode(search.viewMode);
        setIsSavedSearchesDropdownOpen(false);
    };

    // 4. Memos
    const activeFilterCount = useMemo(() => {
        let count = 0;
        if (filters.styles.length > 0) count++;
        if (filters.ageGroups.length > 0) count++;
        if (filters.experienceLevels.length > 0) count++;
        return count;
    }, [filters]);

    // Runtime mirror of the SSG's indexed-tier policy. The build pre-renders a
    // static file (carrying a matching __SSG_DATA__ payload) for exactly the
    // suburb/style routes that are allowed in the index — location hubs with gyms
    // and style pages ≥MIN_GYMS_PER_STYLE. Non-pre-rendered content routes (thin/
    // 0-gym suburbs, sub-threshold styles, removed gyms) are served by Firebase via
    // /notfound.html, which already carries raw noindex.
    //
    // ssGPageData is read once at the initial (fresh) load — exactly how crawlers
    // fetch each URL — so it's present for genuine pre-rendered pages and absent for
    // dead routes. Client-side SPA navigation leaves it stale/absent.
    //
    // WHAT ACTUALLY READS `robots` TODAY: nothing. Since react-helmet-async was removed
    // (2026-07-31) the browser writes no head tags at all — <SeoHead> applies only the
    // tab title — so the served SSG value is the one and only robots directive, and the
    // client can no longer contradict it. `robots` is still computed, and still correct,
    // so that this stays true if head emission is ever revived. Do NOT delete it and do
    // NOT start emitting it without re-reading the note in hooks/useDocumentTitle.ts.
    //
    // The thin-page rule is applied HERE rather than by passing `gymCount` through to
    // getDirectoryMetadata, and that is deliberate: that option also feeds the
    // DESCRIPTION builder ("Find N Boxing gyms in X"), and descriptions are deferred to
    // their own slice by owner decision, so nothing here may reach them.
    //
    // Without this, the runtime robots value defaulted to 'index,follow' on the 411
    // prerendered suburb pages the platform deliberately keeps OUT of Google, because
    // getDirectoryMetadata's thin test needs a count no runtime caller supplied.
    // Only trusted when the payload belongs to THIS route — a stale payload from a
    // previous route would otherwise decide another page's indexability.
    // True when the build prerendered THIS route, i.e. the platform really has a
    // directory here. Also consumed by the empty state below to tell "a suburb we do
    // not cover" apart from "a suburb we cover whose filters exclude everything".
    const isPrerendered = useMemo(() => {
        const routeSuburbSlug = location.pathname.split('/')[2] ?? '';
        return (
            !!ssGPageData &&
            (ssGPageData.type === 'location' || ssGPageData.type === 'style') &&
            ssGPageData.locationSlug === routeSuburbSlug
        );
    }, [location.pathname, ssGPageData]);

    /**
     * ⚠️ L5 (operator-ruled 2026-08-20) — DOES THE PLATFORM HAVE A DIRECTORY FOR THIS SUBURB?
     *
     * DELIBERATELY SEPARATE FROM `isPrerendered` ABOVE, which stays exactly as it was.
     * `isPrerendered` is read by the metadata/indexability path, so widening it would
     * change canonical, postcode and title behaviour — a far larger blast radius than
     * the empty state this fixes. Two signals, two jobs.
     *
     * THE BUG THIS REPLACES: `isPrerendered` reads the SSG payload from an injected
     * <script> tag, once, via useMemo([]) — and DirectoryPage is a PERSISTENT OVERLAY
     * that never remounts. So on any client-side navigation it stays frozen to whichever
     * page was cold-loaded, its locationSlug never matches the new route, and the empty
     * state ALWAYS chose 'no-directory' — telling visitors "we don't cover this suburb"
     * about suburbs we do cover. The 'no-results' copy was unreachable while browsing.
     *
     * ⭐ WHY THE POSTCODE MAP IS THE RIGHT SIGNAL, measured rather than assumed: its
     * entries and the built location routes are an EXACT 1:1 — 988 and 988, zero on
     * either side only, and zero routes whose address disagrees with the map. It is
     * already in the client bundle (it addresses every URL the app builds), so this
     * costs no payload and works identically on a cold load and a client navigation.
     * ⚠️ It answers "was a directory page BUILT for this suburb", which is exactly the
     * question the two empty states disagree about — not "does this place exist".
     */
    const hasDirectoryForSuburb = useMemo(
        () => hasDirectoryFor(filters.suburb, routeStateSlug),
        [filters.suburb, routeStateSlug],
    );

    const metadata = useMemo(() => {
        // Postcode for the tab title (audit O2). READ from the payload, never re-derived
        // here: the authoritative source is the official locality register, which the
        // build can read and the browser cannot. Recomputing from the gym records would
        // disagree with the prerendered <title> and sr-only <h1> on every suburb whose
        // records contradict the register — a hydration mismatch and a wrong tab title.
        //
        // Only trusted when the payload belongs to THIS route; a stale one would print a
        // previous suburb's postcode. Absent (client-side navigation) the title falls back
        // to the previous "Suburb, NSW" form — a tab-only cosmetic difference, since the
        // SSG copy that Google reads always carries it.
        // THE STATE COMES FROM THIS PAGE'S OWN PATH, not from the default — the same
        // B1-class repair as the URL sync in handleApplyFilters above. Omitting the fifth
        // argument defaulted every second-state page's metadata to the FIRST state: the
        // sr-only <h1> below (and the runtime tab title) said NSW on 608 of 1,876
        // prerendered VIC pages, while the build-injected <title> and canonical — whose
        // call sites in vite.config.ts DO pass routeInfo.stateSlug — correctly said VIC,
        // so the page contradicted itself to crawlers.
        // UI-24: routeStateSlug is the ONE canonical derivation (useRouteStateSlug —
        // same classifier, same DEFAULT_STATE arm for the non-directory routes this
        // overlay also mounts on). Replaced the inline re-derivation; in the dep array.
        const base = getDirectoryMetadata(location.pathname, location.search, filters.suburb, {
            postcode: isPrerendered ? ssGPageData!.titlePostcode : undefined,
        }, routeStateSlug);
        const robots = applyRuntimeDirectoryRobots(
            base.robots,
            isPrerendered,
            ssGPageData?.gyms.length ?? 0,
            !!filters.suburb,
        );
        return robots === base.robots ? base : { ...base, robots };
    }, [location, filters.suburb, ssGPageData, isPrerendered, routeStateSlug]);

    // Lazy-load Maps on first need, then latch: flipping shouldLoadMaps back to
    // false destroys the map instance inside GoogleMapView, and the re-created
    // map comes back at the NSW default view (losing the search center/zoom)
    // when toggling Hide Map → Show Map. The map pane is hidden via CSS in
    // LIST mode, so keeping the instance alive is safe.
    const wantsMap = isVisible && viewMode !== ViewMode.LIST;
    const hasWantedMapRef = useRef(false);
    if (wantsMap) hasWantedMapRef.current = true;
    const shouldLoadMaps = wantsMap || hasWantedMapRef.current;
    const shouldBlurMap = !!filters.suburb && (isAutoSearching || !isRevealed || isMapMoving);
    // Results are still "filtering" when the search returned raw gyms but the Web
    // Worker hasn't produced the filtered set yet — treat this as loading so neither
    // the grid nor the header count flashes an empty state during the compute window.
    const resultsFiltering = allGyms.length > 0 && !resultsSettled && sortedGyms.length === 0;
    const isLoading = !!filters.suburb && (isAutoSearching || !isRevealed || resultsFiltering);

    // Location label: prefer the backend's canonical "Suburb Postcode" (e.g. "Alexandria
    // 2015") so the pill shows the postcode just like an in-app search. Two sources supply
    // it: the live search result (resolvedLocation, which arrives only after the round-trip)
    // and an OPTIMISTIC seed carried in navigation state from an in-app search
    // (locationState.resolvedDisplay) — the suburb the entry point already knew the postcode
    // for, so the pill shows "Alexandria 2015" from the FIRST paint with no flick. The seed
    // is a SEPARATE source (not resolvedLocation) so it survives the hook's resolvedLocation=''
    // reset at the start of each search. Each source is admitted only via matchedPostcodeDisplay
    // (exact suburb-part match) so a prefix ("Newcastle West 2302" on the "Newcastle" page) or
    // a stale prior-suburb label is rejected. The seed is gated behind hasMounted so the first
    // (hydration) render matches the SSG HTML, which has no navigation state.
    // The suburb can arrive carrying an embedded state token: a FilterModal/Landing suggestion
    // pick sets it from the backend's "Suburb STATE Postcode" display, and on the in-page modal
    // path that reaches this overlay via router state (the URL-slug path is already clean).
    // Normalise it for DISPLAY ONLY — never for the search key, URL, or SEO metadata — so every
    // path's pill and copy read the canonical "Suburb Postcode" ("Alexandria 2015", not
    // "Alexandria NSW 2015").
    const cleanSuburb = filters.suburb ? normalizeLocationDisplay(filters.suburb) : filters.suburb;
    const seededDisplay = hasMounted
        ? (location.state as { resolvedDisplay?: string } | null)?.resolvedDisplay
        : undefined;
    // O1 — see lib/locationLabel.ts for why the SSG value is preferred at first paint.
    const displaySuburb = resolveLocationLabel({
        cleanSuburb,
        ssgPostcode: ssGPageData?.titlePostcode,
        isPrerendered,
        resolvedLocation,
        seededDisplay,
    });


    // Inline style for the search-progress bar's blue fill, per phase. 'active' is a
    // long ease-out that trickles toward 90% (never lands, since the network end-time
    // is unknown); 'fill' is the deterministic filter-tweak fill; 'hold' snaps to
    // 100% and pauses; 'drain' retracts 100%→0% back to the line; 'idle' is grey.
    const barFillStyle =
        barPhase === 'active'
            ? { transform: 'scaleX(0.9)', opacity: 1, transition: 'transform 9s cubic-bezier(0.12,0.8,0.2,1), opacity 150ms ease' }
            : barPhase === 'fill'
                ? { transform: 'scaleX(1)', opacity: 1, transition: `transform ${FILTER_BAR_MS}ms ease-out, opacity 150ms ease` }
                : barPhase === 'hold'
                    // snap up to 100% (from wherever the trickle reached) and hold at full blue
                    ? { transform: 'scaleX(1)', opacity: 1, transition: 'transform 150ms ease-out' }
                    : barPhase === 'drain'
                        // retract 100%→0% toward the left (origin-left) — mirror of the fill's grow
                        ? { transform: 'scaleX(0)', opacity: 1, transition: `transform ${DRAIN_MS}ms ease-in-out` }
                        : { transform: 'scaleX(0)', opacity: 0, transition: 'none' };

    // DL-4: on the list-only path there is no map to fire onIdle, so lift the
    // map-moving blur as soon as a search settles instead of waiting the 6s
    // fallback. With a map loaded, GoogleMapView's onIdle clears it after re-frame.
    useEffect(() => {
        if (!shouldLoadMaps && isMapMoving && !isAutoSearching) {
            setIsMapMoving(false);
        }
    }, [shouldLoadMaps, isMapMoving, isAutoSearching, allGyms]);

    // DL-6: stable onIdle so GoogleMapView's idle-listener effect doesn't
    // remove+re-add the Google 'idle' listener on every DirectoryPage render.
    const handleMapIdle = useCallback(() => setIsMapMoving(false), [setIsMapMoving]);
    const mapGyms = useMemo(() => sortedGyms.map(g => ({ gymId: g.id, lat: g.lat, lng: g.lng, gymName: g.name })), [sortedGyms]);

    const sortLabels: Record<SortOption, string> = { distance: 'Distance', rating: 'Rating', reviews: 'Review count' };

    return (
        <div
            // Off-route this overlay stays mounted (instant-search keep-alive) but is
            // visually hidden. aria-hidden + inert remove its duplicate <h1> and all
            // its controls from the a11y tree, tab order, and crawler/AT consideration
            // so the route's own landmarks/heading are the only ones seen (G3).
            aria-hidden={!isVisible}
            inert={!isVisible}
            // UI-46: `top-header` at EVERY breakpoint (item 6 moved this off the bare
            // `top-16` onto the shared token). The former `lg:top-0` ran the
            // list/map full-bleed under the desktop auto-hide, which is removed —
            // so content now always starts below the 64px header.
            className={`fixed inset-x-0 bottom-0 top-header bg-surface-canvas ${isVisible ? 'z-10 opacity-100' : 'z-[-1] opacity-0 pointer-events-none'}`}
            style={{ visibility: isVisible ? 'visible' : 'hidden' }}
        >
            {/* UI-50: main landmark for the directory. The id is CONDITIONAL on
                isVisible, and that is load-bearing — this component is a persistent
                overlay mounted ABOVE <Routes> (App.tsx:300), so its markup is baked
                into all 4,221 prerendered pages and appears BEFORE the routed page's
                own <main>. An unconditional id="main-content" here would therefore win
                the skip link on EVERY page and land the user on an element that is
                aria-hidden + inert off-route — worse than having no skip link at all.
                isVisible is route-derived and SSR-evaluated (same value already driving
                aria-hidden/inert above), so the correct id is baked per page. */}
            <main
                id={isVisible ? 'main-content' : undefined}
                className="flex flex-col h-full bg-surface-canvas overflow-hidden"
                style={{ fontFamily: 'Inter, sans-serif' }}
            >
                {/* SEO — the tab title only; see components/SeoHead.tsx.
                    The ItemList JSON-LD that stood alongside this was REMOVED
                    2026-07-31, closing audit N2. It never reached the DOM (Helmet was
                    inert under React 19), and the SSG already emits a COMPLETE
                    ItemList — including the per-gym urls this runtime copy omitted,
                    which is what N2 reported. Emitting it from the browser would give
                    every prerendered directory and style page TWO ItemList blocks, the
                    exact duplication scripts/verify-structured-data.mjs exists to
                    prevent, and that gate inspects built HTML so it would not catch a
                    runtime duplicate. Crawlers fetch these URLs directly and get the
                    SSG's block; nothing is lost. Do not reinstate it. */}
                {isVisible && <SeoHead meta={metadata} />}

                <div className="flex-1 flex overflow-hidden relative bg-surface-canvas">
                    <div className={`flex flex-col bg-surface-canvas overflow-hidden ${isNarrowViewport && viewMode === ViewMode.MAP
                        ? 'absolute inset-0 z-10 bg-transparent pointer-events-none'
                        : viewMode === ViewMode.MAP
                            ? 'w-0 opacity-0 invisible'
                            : viewMode === ViewMode.LIST
                                ? 'w-full opacity-100'
                                : 'w-full md:w-1/2 opacity-100'
                        }`}>
                        <div className={`py-4 flex justify-between items-center flex-shrink-0 sticky top-0 z-40 pointer-events-auto px-4 ${isNarrowViewport && viewMode === ViewMode.MAP ? 'bg-transparent' : 'bg-surface-canvas'
                            }`}>
                            <div className="flex-1 min-w-0 mr-2 md:mr-0 md:flex-none">
                                {!(isNarrowViewport && viewMode === ViewMode.MAP) && (
                                    <>
                                        {/* Mobile: title only (subtitle dropped on mobile to save
                                            space); kept a <span> (not PageTitle's <h1>) to avoid a
                                            second visible h1 alongside the page's sr-only h1. */}
                                        <span className="text-2xl font-semibold leading-tight text-brand-primary md:hidden">
                                            Find a Gym
                                        </span>

                                        {/* Desktop/tablet: title + subtitle (subtitle kept on web only).
                                            Title is a <span> (not PageTitle's <h1>) for the same reason as
                                            mobile above — the page's single h1 is the sr-only metadata title. */}
                                        <div className="hidden md:block">
                                            <span className="block text-2xl font-semibold leading-tight text-brand-primary">
                                                Find a Gym
                                            </span>
                                            {/* ⭐ STATE-AWARE (operator-ruled 2026-08-16): on a state
                                                directory this names THAT state — "…across NSW" on
                                                /nsw/gyms, "…across VIC" on /vic/gyms — instead of
                                                claiming both states on a page that only searches one.
                                                ⚠️ DERIVED from the route's own slug, not two literals
                                                behind a conditional: a third state gets correct copy
                                                with no edit here.

                                                ⚠️⚠️ WIDENED 2026-08-17 (operator list items 6 and 7)
                                                AND THE OLD ANCHOR IS THE WHOLE BUG. This read
                                                `stateRootSlug`, which is `stateDirectoryRootSlug()` —
                                                non-null ONLY on the bare `/{state}/gyms` root. So the
                                                state-aware arm reached 2 routes while every SUBURB,
                                                style, age-group and experience-level page under
                                                /nsw/… and /vic/… fell through to the combined
                                                wording — ~4,100 pages claiming both states while
                                                searching one. The 2026-08-16 ruling was right and its
                                                REACH was two-thousandths of what it read as.

                                                Now keyed on the ROUTE's state segment via
                                                `useRouteStateSlugOrNull`. ⚠️ NOT `useRouteStateSlug`:
                                                that one falls back to DEFAULT_STATE, which would make
                                                the state-less `/gyms` chooser announce "across NSW" —
                                                a false coverage claim on the one page that genuinely
                                                spans both. The null arm is load-bearing, not a
                                                leftover. */}
                                            <p className="text-sm text-text-muted mt-0.5">
                                                {routeStateSlugOrNull
                                                    ? `Martial arts gyms across ${stateSlugToDisplay(routeStateSlugOrNull)}`
                                                    : 'Martial arts gyms across NSW and VIC'}
                                            </p>
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="flex items-center gap-2">
                                {!(isNarrowViewport && viewMode === ViewMode.MAP) && (
                                    <>
                                        <ListboxSelect<SortOption>
                                            label="Sort results"
                                            selectedId={sortBy}
                                            options={(['distance', 'rating', 'reviews'] as SortOption[]).map((o) => ({ id: o, value: o }))}
                                            onSelect={(value) => setSortBy(value)}
                                            panelClassName="w-40"
                                            trigger={({ open, ref, triggerProps }) => (
                                                <Button
                                                    ref={ref}
                                                    variant="outline"
                                                    size="compact"
                                                    aria-label="Sort results"
                                                    {...triggerProps}
                                                    className="flex justify-between"
                                                >
                                                    <span className="hidden md:inline">{sortLabels[sortBy]}</span>
                                                    <ChevronDown size={14} className={`transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
                                                </Button>
                                            )}
                                            renderOption={(opt, { active, selected }) => (
                                                <div className={`px-4 py-2.5 text-sm transition-colors ${selected ? 'bg-brand-primary-subtle text-brand-primary font-semibold' : active ? 'bg-surface-raised text-text-primary' : 'text-text-primary'}`}>
                                                    {sortLabels[opt.value]}
                                                </div>
                                            )}
                                        />

                                        <ListboxSelect<SavedSearch>
                                            label="Saved searches"
                                            open={isSavedSearchesDropdownOpen}
                                            onOpenChange={setIsSavedSearchesDropdownOpen}
                                            selectedId={currentSearchMatchesSaved?.id}
                                            options={savedSearches.map((s) => ({ id: s.id, value: s }))}
                                            onSelect={(search) => handleApplySavedSearch(search)}
                                            panelClassName="w-64"
                                            emptyState={<p className="px-4 py-3 text-sm text-text-muted text-center">No saved searches yet</p>}
                                            trigger={({ open, ref, triggerProps }) => (
                                                <div className={`flex items-center rounded-lg border transition-all min-h-[44px] ${
                                                    currentSearchMatchesSaved
                                                        ? 'bg-brand-primary text-white border-brand-primary hover:bg-brand-primary-hover'
                                                        : 'bg-surface-canvas text-text-primary border-border-default hover:border-text-subtle'
                                                }`}>
                                                    <button
                                                        aria-label={currentSearchMatchesSaved ? 'Unsave search' : 'Save search'}
                                                        onClick={() => {
                                                            if (!user) openAuthModal({ type: 'save-search', gymId: '', returnUrl: location.pathname + location.search });
                                                            else if (currentSearchMatchesSaved) handleUnsaveSearch();
                                                            else if (filters.suburb) setIsSaveSearchModalOpen(true);
                                                            else setIsSavedSearchesDropdownOpen(!isSavedSearchesDropdownOpen);
                                                        }}
                                                        className={`flex items-center gap-2 text-sm font-normal py-2 ${user ? 'pl-3 pr-0' : 'px-3'}`}
                                                    >
                                                        <Bookmark size={16} className={`flex-shrink-0 ${currentSearchMatchesSaved ? 'fill-white' : ''}`} />
                                                        <span className="hidden md:inline">{currentSearchMatchesSaved ? 'Saved' : 'Save'}</span>
                                                    </button>
                                                    {user && (
                                                        <button
                                                            ref={ref}
                                                            {...triggerProps}
                                                            aria-label="Saved searches"
                                                            className="flex items-center pl-2 pr-3 py-2 text-sm"
                                                        >
                                                            <ChevronDown size={14} className={`transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                            renderOption={(opt, { active, selected }) => {
                                                const search = opt.value;
                                                return (
                                                    <div className={`px-4 py-2.5 text-sm transition-colors flex items-center justify-between gap-2 ${selected ? 'bg-brand-primary-subtle text-brand-primary' : active ? 'bg-surface-raised text-text-primary' : 'text-text-primary'}`}>
                                                        <div className="min-w-0 flex-1">
                                                            <span className={`block truncate ${selected ? 'font-semibold' : 'font-medium'}`}>{search.name}</span>
                                                            {/* ⭐ ONE CANONICAL LOCATION LINE (operator-ruled 2026-08-17), replacing the
                                                                " • NSW" tag that shipped hours earlier in UI-104. The tag was appended to
                                                                whatever `location` held, and production holds TWO formats — so it read
                                                                correctly as "Adamstown • NSW" and duplicated the state as
                                                                "Prestons NSW 2170 • NSW". `savedSearchLocationLabel` reconciles both into
                                                                "Suburb STATE POSTCODE" at display time; nothing is written to Firestore.
                                                                ⚠️ The list is still NEVER filtered by state — applying a saved search
                                                                navigates to ITS OWN state (UI-01, `buildSavedSearchUrl`), so a Victorian
                                                                search stays usable from a NSW page in one click. Labelling replaced
                                                                filtering deliberately; do not "tidy" this into a filter.
                                                                ⚠️ The radius suffix is appended HERE, not in the helper: only this surface
                                                                knows it shows radius. `SavedSearchesPage` renders the same label without it. */}
                                                            <span className="text-xs text-text-muted">
                                                                {savedSearchLocationLabel(search)}{search.useRadius ? ` • ${search.radius}km` : ''}
                                                            </span>
                                                        </div>
                                                        {selected && <Check size={16} className="flex-shrink-0" />}
                                                    </div>
                                                );
                                            }}
                                        />

                                        <Button
                                            variant="outline"
                                            size="compact"
                                            aria-label="Open filters"
                                            aria-haspopup="dialog"
                                            className="flex justify-between"
                                            onClick={() => setIsFilterModalOpen(true)}
                                        >
                                            <SlidersHorizontal size={16} className="text-text-primary flex-shrink-0" />
                                            <span className="hidden md:inline font-normal text-text-primary">Filters{activeFilterCount > 0 && ` (${activeFilterCount})`}</span>
                                        </Button>

                                    </>
                                )}
                                <Button
                                    variant="outline"
                                    size="compact"
                                    pressed={isNarrowViewport && viewMode === ViewMode.MAP}
                                    aria-pressed={viewMode !== ViewMode.LIST}
                                    aria-label={isNarrowViewport ? (viewMode === ViewMode.MAP ? 'Show list' : 'Show map') : (viewMode === ViewMode.SPLIT ? 'Hide map' : 'Show map')}
                                    onClick={handleViewToggle}
                                    className="flex justify-between"
                                >
                                    <MapIcon size={16} className="flex-shrink-0" />
                                    <span className="hidden md:inline">
                                        {isNarrowViewport
                                            ? (viewMode === ViewMode.MAP ? 'Show List' : 'Show Map')
                                            : (viewMode === ViewMode.SPLIT ? 'Hide Map' : 'Show Map')
                                        }
                                    </span>
                                </Button>
                            </div>
                        </div>

                        {/* Grey divider — aligned to the header content (mx-4 = header px-4) so it
                            spans from "Find a Gym" to the end of the map button, not the wider card gutter. */}
                        <div className={`h-0.5 relative overflow-hidden bg-border-strong flex-shrink-0 ${viewMode === ViewMode.MAP ? 'hidden' : 'mx-4'}`}>
                            <div className="absolute inset-0 bg-brand-primary origin-left" style={barFillStyle} />
                        </div>

                        <div className={`flex-1 bg-surface-canvas overflow-y-auto ${isNarrowViewport && viewMode === ViewMode.MAP ? 'hidden' : viewMode === ViewMode.LIST ? 'px-8 py-6' : 'p-4'
                            }`}>
                            {/* Semantic H1 for SSG — only rendered when the directory overlay is
                                actually visible (i.e. on a directory/style route).  When
                                isVisible=false the outer div already has visibility:hidden, but
                                the element is still in the DOM and its text is still crawlable,
                                so we suppress it entirely to avoid polluting non-directory pages
                                with a spurious "Find Martial Arts Gyms in NSW" H1. 
                                Note: Visually hidden (sr-only) to avoid duplicating the title in the UI. */}
                            {isVisible && (
                                <h1 className="sr-only">
                                    {/* Directory titles ("Martial Arts Gyms in [Suburb], NSW") carry no
                                        brand suffix, so the H1 is the title verbatim. (A prior strip
                                        targeted a hyphen " - Martial Arts Network" suffix that never
                                        existed here — the house suffix is an en-dash — so it was a no-op.) */}
                                    {metadata.title}
                                </h1>
                            )}
                            {/* Active-filter chips — reflect the APPLIED filters (not the modal draft).
                                The results count / searching indicator leads the row on the left; the
                                chips are pushed to the right edge (a suburb is required for results,
                                and a suburb always renders a chip, so the count never appears without
                                the row). */}
                            {hasActiveFilter(filters) && (
                                <div className={`flex flex-wrap items-center gap-2 mb-4 ${viewMode === ViewMode.LIST ? LIST_VIEW_WIDTH_CAP : ''}`}>
                                    {filters.suburb && (
                                        // basis-full on mobile keeps the count/searching indicator on
                                        // its own line so the chips always wrap below it — consistent
                                        // between the searching and found states (md+ reverts to inline).
                                        <span role="status" aria-live="polite" aria-atomic="true" className="basis-full md:basis-auto">
                                            {isLoading ? (
                                                <SearchingDots />
                                            ) : (
                                                <span className="text-sm text-text-muted whitespace-nowrap">
                                                    {sortedGyms.length === 0
                                                        ? 'No gyms found'
                                                        : `${sortedGyms.length} gym${sortedGyms.length === 1 ? '' : 's'} found`}
                                                </span>
                                            )}
                                        </span>
                                    )}
                                    <div className="flex flex-wrap items-center gap-2 ml-0 md:ml-auto">
                                        {filters.suburb && (
                                            /* Suburb is the search anchor; removing it clears the search
                                               (results require a suburb/gymName) via the canonical clear path. */
                                            <FilterChip label={displaySuburb} onRemove={handleClearAll} />
                                        )}
                                        {filters.useRadius && (
                                            <FilterChip label={`Within ${filters.radius}km`} onRemove={() => removeFilter({ ...filters, useRadius: false })} />
                                        )}
                                        {filters.styles.map(s => (
                                            <FilterChip key={`style-${s}`} label={s} onRemove={() => removeFilter({ ...filters, styles: filters.styles.filter(x => x !== s) })} />
                                        ))}
                                        {filters.ageGroups.map(a => (
                                            <FilterChip key={`age-${a}`} label={a} onRemove={() => removeFilter({ ...filters, ageGroups: filters.ageGroups.filter(x => x !== a) })} />
                                        ))}
                                        {filters.experienceLevels.map(e => (
                                            <FilterChip key={`exp-${e}`} label={e} onRemove={() => removeFilter({ ...filters, experienceLevels: filters.experienceLevels.filter(x => x !== e) })} />
                                        ))}
                                        {filters.gymName && (
                                            <FilterChip label={`Name: ${filters.gymName}`} onRemove={() => removeFilter({ ...filters, gymName: '' })} />
                                        )}
                                    </div>
                                </div>
                            )}


                            {/* M11: heading-level bridge. Each GymCard names its gym in an <h3>, and the
                                empty/loading states also use <h3>, so directory pages jumped h1 → h3 with
                                nothing between (607 suburb + 226 style pages, and /gyms) — breaking
                                heading navigation for screen-reader users. This supplies the MISSING
                                level rather than promoting GymCard's h3, which is shared with Favourites
                                and the landing page and is counted by a Favourites test.
                                Deliberately placed ABOVE the results/loading/empty conditional so it
                                heads whichever branch renders — an earlier version sat inside the results
                                branch only, which left /gyms (an empty state on first paint) still
                                jumping h1 → h3. The label carries the style when one is active, because
                                on a style page "Gyms in Coffs Harbour" would mislabel a Boxing-only list.
                                Visually hidden, like the page's sr-only h1. */}
                            <h2 className="sr-only">
                                {(() => {
                                    const style = filters.styles.length === 1 ? filters.styles[0] : null;
                                    const what = style ? `${style} gyms` : 'Gyms';
                                    return cleanSuburb ? `${what} in ${cleanSuburb}` : what;
                                })()}
                            </h2>

                            {/* 🪦 THE STATE-HUB INTRO PARAGRAPH LIVED HERE — REMOVED 2026-08-16 by
                                operator ruling, and the reason it existed is recorded because the
                                removal ACCEPTS a cost rather than avoiding one.

                                It shipped in deploy #25 to solve a measured problem: without it
                                /nsw/gyms and /vic/gyms are two thin pages whose content reads as
                                near-duplicate. `lib/stateHubIntro.ts`'s guard said so in terms —
                                mirrored text on two thin pages IS the defect — which is why the two
                                states' wording was deliberately NON-mirrored rather than templated.

                                ⚠️ THE OPERATOR WAS SHOWN THIS BEFORE RULING AND REAFFIRMED REMOVAL.
                                Recorded as an ACCEPTED RISK, not an oversight: both pages return to
                                the thin/near-duplicate state the copy was written to fix. A successor
                                finding a duplicate-content flag on these two URLs should start here
                                rather than treating it as a new defect.

                                ⚠️ DO NOT 'restore consistency' by adding one templated sentence to
                                both states — that is the exact shape the original guard forbade. If
                                copy returns, it must differ between states by more than the state
                                name. Copy is the operator's, not this lane's. */}

                            {/* UI-11 (F4, operator-ruled 2026-08-15) — the style-link row.
                                435 style pages were measured at ZERO inbound anchors (0 of
                                43,396 on-site links): reachable from the sitemap, never by
                                clicking. Simulated BFS says linking them from their own
                                suburb rescues 477 pages from click-unreachability.

                                ⚠️ NOT an indexing fix and must not be justified as one —
                                those pages are already sitemapped and NSW measured 224 of
                                225 INDEXED while 100% anchor-orphaned. The defect is human
                                navigation. (Full reasoning: lib/suburbStyleLinks.ts.)

                                ⚠️ ROUTE-SCOPED, and that is load-bearing: the __SSG_DATA__
                                script SURVIVES client-side navigation, so an unscoped read
                                would paint suburb A's styles onto suburb B after an in-app
                                nav. Adopted only when the payload's own locationSlug is the
                                one in the address bar — the same discipline BlogIndexPage
                                applies to its post list.

                                ⚠️ NOT built from the Breadcrumb primitive — and my FIRST
                                stated reason for that was WRONG, corrected here by my own
                                adversarial review rather than left to mislead. I claimed it
                                would trip verify-structured-data.mjs; it would not, because
                                that gate counts JSON-LD entries and the primitive emits none.
                                The real reason: these links are SIBLINGS of this page, not
                                its ancestors, so breadcrumb markup would announce a hierarchy
                                that does not exist. A plain labelled nav is the honest shape. */}
                            {(() => {
                                const links = ssGPageData?.type === 'location'
                                    && ssGPageData.locationSlug
                                    && location.pathname.includes(`/${ssGPageData.locationSlug}/`)
                                    ? (ssGPageData.styleLinks ?? [])
                                    : [];
                                if (links.length === 0) return null;
                                const heading = styleSectionHeading(cleanSuburb || ssGPageData!.locationSlug);
                                return (
                                    <nav aria-label={heading} className="mb-6">
                                        <h3 className="text-sm font-semibold text-text-primary mb-2">{heading}</h3>
                                        <ul className="flex flex-wrap gap-2">
                                            {links.map((l) => (
                                                <li key={l.slug}>
                                                    <Link
                                                        to={l.href}
                                                        className="inline-block rounded-full border border-border-default px-3 py-1 text-sm text-text-muted hover:text-brand-primary hover:border-brand-primary transition-colors"
                                                    >
                                                        {l.label}
                                                    </Link>
                                                </li>
                                            ))}
                                        </ul>
                                    </nav>
                                );
                            })()}

                            {/* 🪦 THE STATE PILLS RENDERED HERE — REMOVED 2026-08-16 (operator-ruled).
                                `{stateRootSlug !== null && <StateDirectorySwitch currentState={stateRootSlug} />}`
                                put a NSW/VIC switch on both state directories. They now appear only on
                                the `/gyms` chooser, whose whole job is that choice.

                                ⚠️ WHAT WENT WITH THEM, because it is not obvious from the markup: these
                                pills were the CRAWLABLE INTERLINK between the two state directories —
                                /nsw/gyms carried the only on-page anchor to /vic/gyms and vice versa.
                                That link is NOT lost: tranche A's footer columns now carry both state
                                directories on every prerendered page, which is strictly more coverage
                                than two pages linking each other. If the footer columns are ever
                                narrowed, re-check this — the interlink would go with them.

                                The component itself stays; the chooser is now its only caller. */}

                            <div
                                aria-busy={isAutoSearching || (!!filters.suburb && !isRevealed) || resultsFiltering}
                                className={`grid gap-5 auto-rows-max ${viewMode === ViewMode.LIST ? `grid-cols-1 md:grid-cols-2 lg:grid-cols-3 ${LIST_VIEW_WIDTH_CAP}` : 'grid-cols-1 lg:grid-cols-2'}`}>
                                {!filters.suburb ? (
                                    /* Filters armed without a location → pulse the CTA: the one
                                       state where the platform is waiting on the user, not the
                                       system (see animate-pulse convention in EmptyState). */
                                    <EmptyState type="no-suburb" onActionClick={() => setIsFilterModalOpen(true)} pulseAction={hasActiveFilter(filters)} />
                                ) : (isAutoSearching || (!!filters.suburb && !isRevealed) || resultsFiltering) ? (
                                    <>
                                        <span className="sr-only" role="status" aria-label="Loading gyms" />
                                        {Array.from({ length: GYMS_PER_PAGE }).map((_, i) => (
                                            <GymCardSkeleton key={i} />
                                        ))}
                                    </>
                                ) : (sortedGyms.length === 0) ? (
                                    /* The list results are final once the loading branch above clears
                                       (search done, revealed, worker settled). The map re-frames
                                       independently — it has its own blur via shouldBlurMap — so we no
                                       longer gate the grid on isMapMoving: doing so showed a 'searching'
                                       state that contradicted the header's "N gyms found" and needlessly
                                       hid ready results until the map's onIdle fired (and stuck if it
                                       never did). */
                                    /* Audit L7: a suburb we have no directory for gets an honest
                                       message and a route out, not "adjust your filters" — no filter
                                       change can produce gyms for a page the build never made. */
                                    <EmptyState
                                        type={hasDirectoryForSuburb ? 'no-results' : 'no-directory'}
                                        suburb={cleanSuburb}
                                        radius={filters.radius}
                                        useRadius={filters.useRadius}
                                        onActionClick={() => setIsFilterModalOpen(true)}
                                    />
                                ) : (
                                    /* UI-127 (loading spec RULED E3) — a wrapper used to sit here and it is GONE,
                                       not merely declassed. It was `<div className="contents animate-in fade-in
                                       duration-300">`, and it was dead TWICE OVER: `animate-in fade-in` needs an
                                       animation plugin this project does not install, AND `display: contents`
                                       means the element generated no box, so it could never have faded even if
                                       the plugin were there. `git log -S 'contents animate-in fade-in'` returns
                                       exactly ONE commit (65e61a7) — the wrapper and the fade arrived together,
                                       so the div existed only to carry the fade.
                                       ⚠️ Removing it is layout-neutral BY CONSTRUCTION, which is why it could be
                                       removed rather than declassed: `display: contents` already made the browser
                                       lay these cards out as direct children of the results grid, and that is
                                       exactly what they are now. Nothing in index.css targets `.contents`. */
                                    paginatedGyms.map((gym, index) => {
                                            const handlers = getGymHandlers(gym);
                                            return (
                                                <div
                                                    key={gym.id}
                                                    ref={(el) => {
                                                        if (el) cardRefs.current.set(gym.id, el);
                                                        else cardRefs.current.delete(gym.id);
                                                    }}
                                                    className="w-full"
                                                >
                                                    <GymCard
                                                        gym={gym}
                                                        index={index}
                                                        // Crawlable anchor, PRE-RENDERED into the static HTML (DirectoryPage
                                                        // is mounted as a persistent overlay above <Routes>). Addressed from
                                                        // the gym's own state so the visible link and this page's ItemList
                                                        // JSON-LD — which already passes the state via lib/ssgSeoAssembly —
                                                        // cannot name different states for the same gym.
                                                        href={buildGymUrl(
                                                            gym.name, gym.stableId || gym.id, gym.suburb, gym.postcode,
                                                            gym.state ? stateDisplayToSlug(gym.state) : DEFAULT_STATE,
                                                        )}
                                                        onClick={handlers.onClick}
                                                        isHighlighted={highlightedGymId === gym.id}
                                                        onMouseEnter={handlers.onMouseEnter}
                                                        onMouseLeave={handlers.onMouseLeave}
                                                        isFavourite={favouriteIds.has(gym.id)}
                                                        onFavouriteToggle={handlers.onFavouriteToggle}
                                                        isFavouriteLoading={loadingFavouriteId === gym.id}
                                                    />
                                                </div>
                                            );
                                        })
                                )}
                                {hasMore && !isAutoSearching && isRevealed && (
                                    <div className="col-span-full flex justify-center py-4">
                                        <Button variant="primary" onClick={handleLoadMore} className="px-6">
                                            Load More ({sortedGyms.length - paginatedGyms.length} remaining)
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className={`relative z-0 h-full ${isNarrowViewport && viewMode === ViewMode.MAP ? 'p-0' : 'p-4'} ${viewMode === ViewMode.LIST ? 'hidden' : 'flex-1 md:w-1/2 opacity-100'}`}>
                        {/* Mobile/tablet map view is full-bleed (no inset/rounded/border); desktop split keeps the inset rounded card. */}
                        <div className={`relative w-full h-full overflow-hidden bg-surface-canvas ${isNarrowViewport && viewMode === ViewMode.MAP ? '' : 'border border-border-default rounded-xl shadow-sm'}`}>
                            <GoogleMapView
                                gyms={mapGyms}
                                center={searchCenter || stateMapCenter}
                                zoom={searchCenter ? 15 : DEFAULT_MAP_ZOOM}
                                onSelectGymId={handleMarkerClick}
                                selectedGymId={highlightedGymId}
                                hoveredGymId={hoveredGymId}
                                onHoverChange={setHoveredGymId}
                                onIdle={handleMapIdle}
                                showMarkers={!shouldBlurMap}
                                triggerResize={`${isVisible}-${isFilterModalOpen}-${shouldBlurMap}-${viewMode}`}
                                radius={filters.radius}
                                shouldLoadMaps={shouldLoadMaps}
                            />
                            {shouldBlurMap && <div className="absolute inset-0 z-50 flex items-center justify-center bg-surface-canvas/80 backdrop-blur-[2px]" />}
                        </div>
                    </div>
                </div>

                <FilterModal
                    isOpen={isFilterModalOpen}
                    onClose={() => setIsFilterModalOpen(false)}
                    onApply={handleApplyFilters}
                    onClearAll={handleClearAll}
                    initialFilters={filters}
                    locationDisplay={displaySuburb}
                    gymCount={sortedGyms.length}
                />

                {/* UI-54: portalled for the SAME reason as FilterModal — this box is
                    confined by the overlay's stacking context above, so its z-[100] never
                    outranked the header's z-50 either. Fixing only the filter panel would
                    have closed half the class and left this one trapped. */}
                {isSaveSearchModalOpen && (
                    <ModalPortal>
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                        <div className="bg-surface-base rounded-2xl p-6 w-full max-w-md mx-4 shadow-xl">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-semibold text-text-primary">Save this search</h2>
                                {/* Modal close: kept as raw <button> rather than IconButton.
                                    Convention is a tight ~22px X with text-shift hover; IconButton's
                                    40px min-size and bg-fill hover would visually dominate the modal. */}
                                <button onClick={() => setIsSaveSearchModalOpen(false)} aria-label="Close save search modal" className="p-1 text-text-subtle hover:text-text-primary"><X size={20} /></button>
                            </div>
                            {/* UI-51: text-subtle (#8A9199) on this dialog's bg-surface-base
                                (#FFFFFF) measures 3.19:1 — it clears the 3:1 NON-TEXT floor but
                                this is body text, which needs 4.5:1. text-muted (#656D76) is
                                5.25:1 here. The sibling close button beside it is icon-only and
                                correctly PASSES at 3.19:1; the 2.97:1 once recorded against it
                                was measured on the canvas surface, not this dialog's white. */}
                            <p className="text-sm text-text-muted mb-4">{cleanSuburb}{filters.useRadius ? ` • ${filters.radius}km` : ''}</p>
                            <Input type="text" aria-label="Search name" value={saveSearchName} onChange={(e) => setSaveSearchName(e.target.value)} placeholder="Search name (optional)" className="mb-4" />
                            <div className="flex gap-3">
                                <Button variant="outline" onClick={() => setIsSaveSearchModalOpen(false)} className="flex-1">
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={handleSaveSearch}
                                    isLoading={isSavingSearch}
                                    className="flex-1"
                                >
                                    Save search
                                </Button>
                            </div>
                        </div>
                    </div>
                    </ModalPortal>
                )}
            </main>
        </div>
    );
};

export default DirectoryPage;
